// Auth headers are injected globally by ApiHeadersInterceptor.
// This service handles all /auth/* API calls and owns the session in localStorage.
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { EnvService } from './env/env.service';
import { ScKryptoService } from './ScKryptoService';

// ── Storage key (must match the key used in ApiHeadersInterceptor) ─────────────
const SESSION_KEY = 'holl_auth_session';

// ── API request shapes ─────────────────────────────────────────────────────────

export interface LoginPayload {
  email: string;
  password: string;
}

export interface MfaRequestPayload {
  email: string;
  channel: 'email' | 'phone';
}

export interface MfaVerifyPayload {
  email: string;
  otp: string;
}

export interface ChangePasswordPayload {
  email: string;
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  responseCode: string;
  responseMessage: string;
}

export interface ResetPasswordResponse {
  responseCode: string;
  responseMessage: string;
  data: any;
}

export interface VerifyResetTokenResponse {
  responseCode: string;
  responseMessage: string;
}

export interface UpdatePasswordResponse {
  responseCode: string;
  responseMessage: string;
}

export interface RefreshPayload {
  refreshToken: string;
}

// ── API response shapes ────────────────────────────────────────────────────────

export interface LoginResponse {
  responseCode: string;
  responseMessage: string;
  email: string;
  mfaRequired: boolean;
}

export interface MfaRequestResponse {
  responseCode: string;
  responseMessage: string;
  data: any;
}

export interface ApiUser {
  userID: number;
  fullName: string;
  email: string;
  phone: string;
  userType: string;
  status: string;
  agentCode: string | null;
  brokerCode: string | null;
  clientCode: string | null;
  defaultPassword: boolean;
  [key: string]: any;
}

export interface MfaVerifyResponse {
  responseCode: string;
  responseMessage: string;
  data: any;
  token: string | null;
  user: ApiUser | null;
  refreshToken: string | null;
  expiresDate: string | null;
}

export interface RefreshResponse {
  responseCode: string;
  responseMessage: string;
  newJwtToken: string | null;
  refreshToken: string | null;
  expiresDate: string | null;
}

// ── Session shape stored in localStorage ──────────────────────────────────────

export interface AuthSession {
  token: string;
  refreshToken: string;
  expiresDate: string;
  user: ApiUser;
}

// ──────────────────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private baseUrl: string;

  constructor(private http: HttpClient, private env: EnvService, private krypto: ScKryptoService) {
    this.baseUrl = this.env.apiUrl + '/auth';
  }

  // ── Step 1: Validate credentials ──────────────────────────────────────────

  /**
   * POST /auth/login
   * Validates email + password. On success mfaRequired=true — route to OTP.
   */
  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.baseUrl + '/login', payload).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Step 2: Request OTP ───────────────────────────────────────────────────

  /**
   * POST /auth/mfa/request
   * Dispatches a one-time code to the chosen channel (email | phone).
   */
  requestMfa(payload: MfaRequestPayload): Observable<MfaRequestResponse> {
    return this.http.post<MfaRequestResponse>(this.baseUrl + '/mfa/request', payload).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Step 3: Verify OTP & open session ─────────────────────────────────────

  /**
   * POST /auth/mfa/verify
   * Validates the OTP. On responseCode "000" the JWT + user are persisted.
   */
  verifyMfa(payload: MfaVerifyPayload): Observable<MfaVerifyResponse> {
    return this.http.post<MfaVerifyResponse>(this.baseUrl + '/mfa/verify', payload).pipe(
      tap((res) => {
        if (res.responseCode === '000' && res.token && res.user) {
          var u = res.user;

          // ── Primary session (read by AuthService helpers & interceptor) ──
          var session: AuthSession = {
            token:        res.token,
            refreshToken: res.refreshToken || '',
            expiresDate:  res.expiresDate  || '',
            user:         u
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));

          // ── Legacy appuser key (read by DashboardService.currentUser()
          //    and the Toolbar/Sidebar components) ──────────────────────────
          // Map the new API user shape to the shape the toolbar expects:
          // { first_name, last_name, email, phone, code, role, agency }
          var nameParts  = (u.fullName || '').trim().split(' ');
          var first_name = nameParts[0] || '';
          var last_name  = nameParts.slice(1).join(' ') || first_name;
          // bank_id=10 is the backend sentinel for "super admin / no specific bank" —
          // treat it exactly the same as a null bank on the frontend.
          var isSuperAdminBank = !u.bank || u.bank.bankID === 10;
          var legacyUser = {
            first_name:   first_name,
            last_name:    last_name,
            email:        u.email,
            phone:        u.phone,
            code:         u.agentCode || u.brokerCode || u.clientCode || String(u.userID),
            role:         this.mapUserTypeToRole(u.userType),
            agency:       isSuperAdminBank ? null : u.bank.bankID,
            bankName:     isSuperAdminBank ? '' : (u.bank.bankName    || u.bank.name         || ''),
            bankLogoUrl:  isSuperAdminBank ? '' : (u.bank.bankLogoUrl || u.bank.logoUrl      || '')
          };
          localStorage.setItem('appuser', this.krypto.encrypt(JSON.stringify(legacyUser)));

          // ── Legacy login flag (read by DashboardService.isLoggedIn) ──────
          localStorage.setItem('isLoggedInStatus', 'true');
        }
      }),
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Step 4: Change default password (first-login flow) ───────────────────

  /**
   * POST /auth/change-password
   * Called when defaultPassword === true after OTP verification.
   * Requires a valid Bearer token (already stored in the session).
   */
  changePassword(payload: ChangePasswordPayload): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(this.baseUrl + '/change-password', payload).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Forgot password: request reset link ───────────────────────────────────

  /**
   * POST /auth/reset-password
   * Sends a password reset link to the given email address.
   */
  resetPassword(email: string): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(this.baseUrl + '/reset-password', { email }).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Forgot password: verify reset token ───────────────────────────────────

  /**
   * POST /auth/reset-password/token/verify
   * Validates the token from the reset link before showing the new-password form.
   */
  verifyResetToken(resetToken: string): Observable<VerifyResetTokenResponse> {
    return this.http.post<VerifyResetTokenResponse>(this.baseUrl + '/reset-password/token/verify', { resetToken }).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Forgot password: set new password ─────────────────────────────────────

  /**
   * POST /auth/update-password
   * Sets the new password using the validated reset token.
   */
  updatePassword(resetToken: string, newPassword: string): Observable<UpdatePasswordResponse> {
    return this.http.post<UpdatePasswordResponse>(this.baseUrl + '/update-password', { resetToken, newPassword }).pipe(
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Token refresh ──────────────────────────────────────────────────────────

  /**
   * POST /auth/refresh
   * Exchanges the stored refresh token for a new JWT and updates the session.
   */
  refreshToken(): Observable<RefreshResponse> {
    var session = this.getSession();
    if (!session || !session.refreshToken) {
      return throwError('No refresh token available.');
    }
    var payload: RefreshPayload = { refreshToken: session.refreshToken };
    return this.http.post<RefreshResponse>(this.baseUrl + '/refresh', payload).pipe(
      tap(function(res) {
        if (res.responseCode === '000' && res.newJwtToken && session) {
          session.token        = res.newJwtToken;
          session.refreshToken = res.refreshToken  || session.refreshToken;
          session.expiresDate  = res.expiresDate   || session.expiresDate;
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        }
      }),
      catchError(function(err) { return throwError(err); })
    );
  }

  // ── Session helpers ────────────────────────────────────────────────────────

  /** Returns the full persisted session, or null. */
  getSession(): AuthSession | null {
    var raw = localStorage.getItem(SESSION_KEY);
    if (!raw) { return null; }
    try {
      return JSON.parse(raw) as AuthSession;
    } catch (e) {
      return null;
    }
  }

  /** Returns the JWT bearer token, or null. */
  getToken(): string | null {
    var session = this.getSession();
    return session ? session.token : null;
  }

  /** Returns the logged-in user object, or null. */
  getUser(): ApiUser | null {
    var session = this.getSession();
    return session ? session.user : null;
  }

  /** True when a valid session with a token exists. */
  get isLoggedIn(): boolean {
    var session = this.getSession();
    return !!(session && session.token);
  }

  /** Wipes the session — call on logout. */
  clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('isLoggedInStatus');
    localStorage.removeItem('appuser');
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Maps the API's userType string to the exact role key used in roles.mockup.ts.
   *
   * API userType  →  roles.mockup.ts role
   * ─────────────────────────────────────
   * ADMIN         →  'ADMIN'
   * AGENT         →  'agent'
   * BROKER_USER   →  'broker-user'
   * BROKER_ADMIN  →  'broker-admin'
   * CLIENT        →  'client'
   */
  private mapUserTypeToRole(userType: string): string {
    var map: { [key: string]: string } = {
      'ADMIN':        'ADMIN',
      'AGENT':        'AGENT',
      'BROKER_USER':  'BROKER_USER',
      'BROKER_ADMIN': 'BROKER_ADMIN',
      'CLIENT':       'CLIENT'
    };
    return map[userType] || (userType ? userType.toUpperCase() : 'CLIENT');
  }
}
